import React, { useCallback, useContext } from "react";

import { Slide, useMediaQuery, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import FoodDatabase from "@/components/FoodDatabase";
import MealPatternDatabase from "@/components/FoodDatabase/MealPatternDatabase";
import {
  PlannedFood,
  PlannedFoodPortion,
} from "@/components/FoodDatabase/utils";
import {
  FoodAddPortionDetailsFragment,
  FoodItemFragment,
  MealPatternNameKind,
} from "@/gql/graphql";

import MobileCellCart from "./MobileCellCart";
import MealPlanFormStore from "../store/MealPlanFormStore";

const useStyles = makeStyles()((theme) => ({
  foodDatabase: {
    alignSelf: "flex-start",
    position: "sticky",
    top: theme.spacing(4),
    width: "calc(30% - 12px)",
    marginLeft: theme.spacing(2),
    verticalAlign: "top",
    zIndex: 10,
  },
  foodDatabaseOpen: {
    display: "block",
  },
  foodDatabaseClose: {
    display: "none",
  },
  // On mobile the 30% sidebar is unusable, so the search panel becomes a
  // full-screen overlay that slides up. It stays inside the ClickAwayListener
  // subtree (no portal) so interacting with it doesn't clear the active cell.
  mobileOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.common.white,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    // Leave room for the collapsed cart bar pinned to the bottom.
    paddingBottom: theme.spacing(12),
  },
}));

const SearchFoodPanel: React.FC = observer(() => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const store = useContext(MealPlanFormStore.Context);
  const activeCell = store.cells.active;
  const foodDatabaseOpen = !!activeCell;

  const handleAddFood = useCallback(
    (
      food: FoodItemFragment,
      foodPortion: FoodAddPortionDetailsFragment,
      amount: number
    ) => {
      if (activeCell == null) return;
      // A food picked from "My Recipes" is added as an expandable recipe
      // group with its ingredients copied in, not as a single opaque item.
      if (food.source === "recipes" && food.sourceId) {
        void store.addRecipeToCell(activeCell, food, foodPortion, amount);
        return;
      }
      activeCell.addPlannedMealItem(food, foodPortion, amount);
    },
    [activeCell, store]
  );

  const handleAddMealPattern = useCallback(
    (
      name: MealPatternNameKind,
      amountServings: number,
      food: PlannedFood,
      foodPortion: PlannedFoodPortion
    ) => {
      if (activeCell == null) return;
      activeCell.addMealPatternItem(name, amountServings, food, foodPortion);
    },
    [activeCell]
  );

  const panelContent =
    activeCell?.mode === "meal_pattern_item" ? (
      <MealPatternDatabase
        onClose={() => store.cells.clearActive()}
        onAddMealPattern={handleAddMealPattern}
      />
    ) : (
      <FoodDatabase
        onClose={() => store.cells.clearActive()}
        onAddFood={handleAddFood}
        clientAllergies={store.clientAllergies}
      />
    );

  if (isMobile) {
    return (
      <>
        <Slide in={foodDatabaseOpen} direction="up" mountOnEnter unmountOnExit>
          <div className={classes.mobileOverlay}>{panelContent}</div>
        </Slide>
        {foodDatabaseOpen && <MobileCellCart />}
      </>
    );
  }

  return (
    <Slide in={foodDatabaseOpen} direction="left">
      <div
        className={cx(classes.foodDatabase, {
          [classes.foodDatabaseOpen]: foodDatabaseOpen,
          [classes.foodDatabaseClose]: !foodDatabaseOpen,
        })}
      >
        {panelContent}
      </div>
    </Slide>
  );
});

export default SearchFoodPanel;
